import React, { useEffect, useRef } from "react";
import "./WebLottiePlayer.css";

export type WebLottieSource = string | Record<string, unknown>;

const loadAnimationFromUrl = async (
  url: string,
): Promise<Record<string, unknown>> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load Lottie animation (${response.status})`);
  }
  return response.json();
};

interface WebLottiePlayerProps {
  source: WebLottieSource;
  className?: string;
  style?: React.CSSProperties;
  autoPlay?: boolean;
  loop?: boolean;
  onClick?: () => void;
}

/**
 * Web Lottie player — requires `lottie-web` in your React web project:
 * npm install lottie-web
 */
const WebLottiePlayer: React.FC<WebLottiePlayerProps> = ({
  source,
  className = "",
  style,
  autoPlay = true,
  loop = true,
  onClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<{ destroy: () => void } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (
          source === null ||
          source === undefined ||
          (typeof source === "string" && !source.trim())
        ) {
          return;
        }

        const lottieModule = await import("lottie-web");
        const lottie = lottieModule.default;
        if (cancelled || !containerRef.current) {
          return;
        }

        animationRef.current?.destroy();

        const baseConfig = {
          container: containerRef.current,
          renderer: "svg" as const,
          loop,
          autoplay: autoPlay,
        };

        if (typeof source === "string") {
          if (
            source.startsWith("http") ||
            source.startsWith("/") ||
            source.startsWith("data:")
          ) {
            const animationData = await loadAnimationFromUrl(source);
            if (cancelled || !containerRef.current) {
              return;
            }
            animationRef.current = lottie.loadAnimation({
              ...baseConfig,
              animationData,
            });
          } else {
            animationRef.current = lottie.loadAnimation({
              ...baseConfig,
              animationData: JSON.parse(source),
            });
          }
        } else {
          animationRef.current = lottie.loadAnimation({
            ...baseConfig,
            animationData: source,
          });
        }
      } catch (error) {
        console.warn(
          "[WebLottiePlayer] Install lottie-web in your web app: npm install lottie-web",
          error,
        );
      }
    };

    load();

    return () => {
      cancelled = true;
      animationRef.current?.destroy();
      animationRef.current = null;
    };
  }, [source, autoPlay, loop]);

  return (
    <div
      ref={containerRef}
      className={`web-lottie-player ${className}`}
      style={style}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                onClick();
              }
            }
          : undefined
      }
    />
  );
};

export default WebLottiePlayer;
